import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  List,
  ListItem,
  Paper,
  Divider,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import axios from "../../../api/axios";
import { useApiRequest } from "../../../api/axios";
import {
  EQUIPMENT_COUNTERS_UNVERIFIED_ENDPOINT,
  EQUIPMENT_COUNTERS_VERIFY_ENDPOINT,
  SUCCESS_SNACKBAR,
  ERROR_SNACKBAR,
} from "../../../consts";
import CustomCircularProgress from "../../ui/customCircularProgress/customCircularProgress.component";
import { formatCounterValue, formatDateTime } from "../../../utils";

export interface UnverifiedCounter {
  id: string;
  kind_display: string;
  provider_display: string;
  value: string;
  read_at: string;
  equipment: {
    id: string;
    name: string;
    serial: string;
    full_name: string;
  } | null;
}

interface VerifyCountersProps {
  onSuccess?: () => void;
  equipmentId?: string;
}

export const VerifyCounters = ({ onSuccess, equipmentId }: VerifyCountersProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, executeRequest } = useApiRequest();
  const [counters, setCounters] = useState<UnverifiedCounter[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loadingCounters, setLoadingCounters] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");

  const fetchCounters = async () => {
    setLoadingCounters(true);
    try {
      const params: Record<string, string> = {};
      if (equipmentId) params.equipment_id = equipmentId;
      if (search.trim()) params.search = search.trim();
      const response = await axios.get(
        EQUIPMENT_COUNTERS_UNVERIFIED_ENDPOINT,
        { params }
      );
      const results = Array.isArray(response.data) ? response.data : [];
      setCounters(results);
      setSelectedIds(new Set());
    } catch (error) {
      enqueueSnackbar(t("Failed to fetch unverified counters"), ERROR_SNACKBAR);
    } finally {
      setLoadingCounters(false);
    }
  };

  useEffect(() => {
    fetchCounters();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when filters change only
  }, [equipmentId, search]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleToggle = (counterId: string) => {
    const next = new Set(selectedIds);
    if (next.has(counterId)) {
      next.delete(counterId);
    } else {
      next.add(counterId);
    }
    setSelectedIds(next);
  };

  const handleSelectAll = () => {
    if (selectedIds.size === counters.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(counters.map((c) => c.id)));
    }
  };

  const handleVerify = async () => {
    if (selectedIds.size === 0) {
      enqueueSnackbar(
        t("Please select at least one counter"),
        ERROR_SNACKBAR
      );
      return;
    }

    const result = await executeRequest(() =>
      axios.post(EQUIPMENT_COUNTERS_VERIFY_ENDPOINT, {
        counter_ids: Array.from(selectedIds),
      })
    );

    if (result?.success) {
      enqueueSnackbar(t("Counters marked as verified"), SUCCESS_SNACKBAR);
      onSuccess?.();
    } else {
      enqueueSnackbar(t("Failed to verify counters"), ERROR_SNACKBAR);
    }
  };

  const getDisplayText = (counter: UnverifiedCounter): string => {
    const parts = [
      counter.equipment?.full_name ?? "",
      counter.kind_display,
      `${t("Value")}: ${formatCounterValue(counter.value)}`,
      `${t("Read at")}: ${formatDateTime(counter.read_at)}`,
    ].filter(Boolean);
    return parts.join(" · ");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <TextField
        size="small"
        placeholder={t("Search by equipment name or serial")}
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        fullWidth
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t("Select counters to mark as verified")}
        </Typography>
        <Button
          size="small"
          onClick={handleSelectAll}
          disabled={loading || counters.length === 0}
        >
          {selectedIds.size === counters.length && counters.length > 0
            ? t("Deselect all")
            : t("Select all")}
        </Button>
      </Box>

      {loadingCounters ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CustomCircularProgress />
        </Box>
      ) : counters.length === 0 ? (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t("No unverified counters at this time.")}
          </Typography>
        </Box>
      ) : (
        <Paper sx={{ maxHeight: 400, overflow: "auto" }}>
          <List>
            {counters.map((counter, index) => (
              <div key={counter.id}>
                <ListItem>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedIds.has(counter.id)}
                        onChange={() => handleToggle(counter.id)}
                        disabled={loading}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">
                          {getDisplayText(counter)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < counters.length - 1 && <Divider />}
              </div>
            ))}
          </List>
        </Paper>
      )}

      <Button
        variant="contained"
        onClick={handleVerify}
        disabled={loading || selectedIds.size === 0 || loadingCounters}
        fullWidth
      >
        {loading
          ? t("Verifying...")
          : t("Mark as verified ({{count}} selected)", {
              count: selectedIds.size,
            })}
      </Button>
    </Box>
  );
};
